import {Component, computed, DestroyRef, inject, model, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogContent, MatDialogTitle} from "@angular/material/dialog";
import {DragDropModule} from "../../../drag-drop/drag-drop.module";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {
    MatDatepickerToggle,
    MatDateRangeInput,
    MatDateRangePicker,
    MatEndDate,
    MatStartDate
} from "@angular/material/datepicker";
import {MatError, MatFormField, MatInput, MatLabel, MatSuffix} from "@angular/material/input";
import {MAT_DATE_LOCALE, MatOption, provideNativeDateAdapter} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {MatStep, MatStepLabel, MatStepper, MatStepperNext, MatStepperPrevious} from "@angular/material/stepper";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'phx-popup-form',
    imports: [
        MatDialogContent,
        MatDialogTitle,
        DragDropModule,
        MatButtonToggle,
        MatButtonToggleGroup,
        MatDateRangeInput,
        MatDateRangePicker,
        MatDatepickerToggle,
        MatEndDate,
        MatError,
        MatFormField,
        MatInput,
        MatLabel,
        MatOption,
        MatSelect,
        MatStartDate,
        MatStep,
        MatStepLabel,
        MatStepper,
        MatStepperNext,
        MatStepperPrevious,
        MatSuffix,
        ReactiveFormsModule
    ],
  templateUrl: './popup-form.component.html',
    providers: [
        provideNativeDateAdapter(),
        { provide: MAT_DATE_LOCALE, useValue: 'de-DE' }
    ],
  styleUrl: './popup-form.component.scss'
})
export class PopupFormComponent {
    // ── DI via inject() ────────────────────────────────────────────────────────
    private readonly fb        = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    protected CDN = inject('CDN' as any) as string;
    data = inject<{ dialog?: any, mode: 'create' | 'edit' }>(MAT_DIALOG_DATA);

    invalid: any;

    // ── Signals ────────────────────────────────────────────────────────────────
    readonly notification    = signal(false);
    readonly success         = signal('');
    readonly error           = signal('');
    readonly imagePreview = signal<string | null>(null);
    readonly idfListFile     = signal<File | null>(null);

    imageUrl = computed(() => {
        const preview = this.imagePreview();
        if (!preview) {
            return '';
        }
        if (preview.startsWith('data:')) {
            return preview;
        }
        return `${this.CDN}/marketing-management/${preview}`;
    });

    title = computed(() =>
        this.data.mode === 'create' ? 'Neue Banner Ad' : 'Banner Ad bearbeiten'
    );

    /** two-way bindable via model() — parent can also bind [(recipientMode)]="..." */
    recipientMode = model<'filter' | 'idf'>('filter');

    // ── Typed form ─────────────────────────────────────────────────────────────
    popupFormGroup!: FormGroup;

    private image: File | null = null;

    // ── Lifecycle ──────────────────────────────────────────────────────────────
    ngOnInit(): void {
        this.popupFormGroup = this.fb.group({
            // Step 2 – Content
            type:       [''],
            title:      ['', Validators.required],
            link:       [''],
            open_in:    ['new_tab'],
            body:       [''],
            disclaimer: [''],
            button:     [''],
            highlight:  [''],
            image_alt:  [''],

            // Step 1 – Settings
            priority:           [''],
            page_filter:        [''],
            start:              [null],
            end:                [null],
            active:             ['true'],
            public:             ['true'],
            owner_only:         ['false'],
            show_for_internals: ['false'],
            subscription:       ['false'],
            condition:          ['IN'],

            // Step 3 – Recipients
            distribution_centers: [[]],
            vis_authorities:      [[]],
            regions:              [[]],
        });

        // Example: react to form value changes with automatic cleanup
        this.popupFormGroup.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                // reset notification on every change
                if (this.notification()) {
                    this.notification.set(false);
                }
            });
    }

    // ── Event handlers ─────────────────────────────────────────────────────────
    onImageSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.image = input.files?.[0] ?? null;

        if (this.image) {
            const reader = new FileReader();
            reader.onload = (e) => this.imagePreview.set(e.target?.result as string);
            reader.readAsDataURL(this.image);
        } else {
            this.imagePreview.set(null);
        }
    }

    onIdfListUpload(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.idfListFile.set(input.files?.[0] ?? null);
    }

    async onSubmit(): Promise<void> {
        if (this.popupFormGroup.invalid) {
            this.popupFormGroup.markAllAsTouched();
            return;
        }

        try {
            let imageUrl = '';
            if (this.image) {
                imageUrl = await this.uploadImageToAzure(this.image);
            }

            const payload = {
                ...this.popupFormGroup.value,
                imageUrl,
                recipientMode: this.recipientMode(),
                idfListFile: this.recipientMode() === 'idf' ? this.idfListFile() : null,
            };

            console.log('Saving popup:', payload);
            this.success.set('Dialog successfully saved.');
            this.error.set('');
            this.notification.set(true);
        } catch {
            this.error.set('Failed to save dialog. Please try again.');
            this.success.set('');
            this.notification.set(true);
        }
    }

    // ── Private helpers ────────────────────────────────────────────────────────
    private async uploadImageToAzure(_file: File): Promise<string> {
        // TODO: implement real Azure upload
        return Promise.resolve('');
    }

    clearImageSelection() {
        this.imagePreview.set('');
        // this.adsForm.patchValue({image: null});
        // this.adsForm.get('image')?.markAsTouched();
        // this.adsForm.get('image')?.updateValueAndValidity();
    }
}
